import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, ArrowRight, CheckCircle2, AlertTriangle, Circle, RefreshCw, Loader2 } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const PERIODS = [["all", "Tudo"], ["hoje", "Hoje"], ["semana", "Semana"], ["mes", "Mês"], ["ano", "Ano"]];

export default function AdminPix() {
  const { user } = useAuth();
  const canRetry = user?.permissions?.includes("finance:write");
  const [period, setPeriod] = useState("all");
  const [q, setQ] = useState("");
  const [txns, setTxns] = useState([]);
  const [detail, setDetail] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/pix/transactions", { params: { period, q: q || undefined } })
      .then((r) => setTxns(r.data.transactions)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [period, q]);
  useEffect(() => { load(); }, [load]);

  const refreshDetail = useCallback(async (id) => {
    try { const { data } = await api.get(`/admin/pix/transactions/${id}`); setDetail(data); } catch { /* silent */ }
  }, []);

  // Auto-refresh do detalhe enquanto o diálogo está aberto (reflete webhook/sync sem recarregar).
  useEffect(() => {
    if (!detail?.id) return undefined;
    const id = detail.id;
    const t = setInterval(() => { refreshDetail(id); load(); }, 8000);
    return () => clearInterval(t);
  }, [detail?.id, refreshDetail, load]);

  const open = async (id) => {
    try { const { data } = await api.get(`/admin/pix/transactions/${id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const retryPayouts = async () => {
    const id = detail.id;
    setRetrying(true);
    try {
      const { data } = await api.post(`/admin/pix/transactions/${id}/retry-payouts`);
      toast.success(`Reprocessando ${data.retried} repasse(s)... aguardando confirmação do Asaas.`);
      await refreshDetail(id); load();
      // Consulta o desfecho por alguns segundos (o Asaas pode levar instantes para concluir).
      let n = 0;
      const poll = setInterval(async () => { n += 1; await refreshDetail(id); load(); if (n >= 6) clearInterval(poll); }, 5000);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRetrying(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-pix">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map(([v, l]) => (
          <button key={v} data-testid={`pix-period-${v}`} onClick={() => setPeriod(v)} className={`px-3 py-1.5 rounded-xl text-sm border ${period === v ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/10 text-zinc-400"}`}>{l}</button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="pix-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por evento, organizador, ID..." className="bg-[#09090B] border-white/10 rounded-xl pl-9" />
        </div>
      </div>

      <div className="bg-card border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="max-h-[520px] overflow-auto divide-y divide-white/[0.04]">
          {txns.length === 0 ? <p className="p-8 text-center text-sm text-zinc-500">Nenhuma transação.</p> :
            txns.map((t) => (
              <button key={t.id} data-testid={`pix-row-${t.id}`} onClick={() => open(t.id)} className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-sm">{t.event_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{t.organizer_name} · {t.provider} · {(t.created_at || "").slice(0, 10)} · {t.recipients} músico(s)</div>
                </div>
                {!t.reconciled && t.status === "PAID" && <span title="Divergência de conciliação" className="text-red-400"><AlertTriangle className="h-4 w-4" /></span>}
                {t.payouts_failed > 0 && <span className="text-[10px] px-2 py-0.5 rounded-xl border border-red-500/40 text-red-400 bg-red-500/10">{t.payouts_failed} falha(s)</span>}
                <span className="mono text-accent shrink-0">{money(t.total)}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-xl border shrink-0 ${t.status === "PAID" ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : t.status === "FAILED" || t.status === "EXPIRED" ? "border-red-500/40 text-red-400 bg-red-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>{t.status_label}</span>
              </button>
            ))}
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[85vh] overflow-auto" data-testid="pix-detail">
          <DialogHeader><DialogTitle>Rastreio da transação Pix</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              {/* Origem → Destino */}
              <div className="flex items-center gap-3 text-sm">
                <div className="flex-1 bg-[#09090B] border border-white/10 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Origem (organizador)</div>
                  <div className="font-medium">{detail.origem.organizer_name}</div>
                  <div className="text-xs text-zinc-500">{detail.origem.event_name}</div>
                  <div className="mono text-accent mt-1">{money(detail.origem.valor)}</div>
                  <div className="text-[10px] text-zinc-600 mt-1 break-all">Cobrança: {detail.origem.charge_id || "—"}</div>
                </div>
                <ArrowRight className="h-5 w-5 text-zinc-600 shrink-0" />
                <div className="flex-1 bg-[#09090B] border border-white/10 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Destinos ({detail.destinos.length})</div>
                  {detail.destinos.map((d) => (
                    <div key={d.user_id} data-testid={`pix-dest-${d.user_id}`} className="flex items-start justify-between gap-2 text-xs py-1 border-b border-white/[0.03] last:border-0">
                      <div className="min-w-0">
                        <div className="truncate">{d.stage_name}</div>
                        <div className="text-zinc-600 mono text-[10px] truncate">{d.pix_key_masked}</div>
                      </div>
                      <span className="shrink-0 text-right"><span className="mono">{money(d.net)}</span><br /><span className={d.payout_status === "PAID" ? "text-emerald-400" : d.payout_status === "FAILED" ? "text-red-400" : "text-amber-400"}>{d.payout_label}</span></span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Conciliação */}
              <div className={`rounded-xl border p-3 ${detail.reconciliation.ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="pix-reconciliation">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Conciliação {detail.reconciliation.ok ? "" : "— DIVERGÊNCIA"}</div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                  {[["Entrada", detail.reconciliation.entrada], ["Destinos", detail.reconciliation.destinos], ["Taxa plataforma", detail.reconciliation.taxa_plataforma], ["Taxa financeira", detail.reconciliation.taxa_financeira], ["Diferença", detail.reconciliation.diferenca]].map(([l, v]) => (
                    <div key={l}><div className="text-zinc-500">{l}</div><div className="mono">{money(v)}</div></div>
                  ))}
                </div>
                <div className="flex items-center gap-4 text-xs mt-2 pt-2 border-t border-white/10 flex-wrap">
                  <span className="text-zinc-500">Saídas: <span className="mono text-zinc-300">{money(detail.reconciliation.saidas)}</span></span>
                  <span className="text-zinc-500">Taxa do gateway (Asaas): <span className="mono text-zinc-300">{money(detail.reconciliation.taxa_gateway)}</span></span>
                  <span className="text-zinc-500">Receita líquida da plataforma: <span className="mono text-emerald-400">{money(detail.reconciliation.receita_liquida)}</span></span>
                </div>
                {detail.gateway_fee == null && detail.origem.status === "PAID" && <p className="text-[11px] text-amber-400 mt-1">Taxa do gateway não retornada pelo Asaas nesta consulta.</p>}
              </div>

              {/* Reprocessar repasses com falha (Super Admin) */}
              {canRetry && detail.origem.status === "PAID" && detail.destinos.some((d) => ["FAILED", "AGUARDANDO_SALDO", "PENDING", "NOT_ELIGIBLE", "AGUARDANDO_AUTORIZACAO"].includes(d.payout_status)) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="retry-payouts-btn" disabled={retrying} className="w-full bg-amber-600 hover:bg-amber-700 rounded-xl gap-2">{retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}{retrying ? "Reprocessando repasse..." : "Reprocessar repasse"}</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-card border-amber-500/30">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-amber-400">Reprocessar repasses?</AlertDialogTitle>
                      <AlertDialogDescription>Isso reenvia via Asaas (dinheiro real) os repasses ainda não concluídos desta transação. Repasses já pagos ou em processamento são ignorados. Requer chave Pix válida e saldo na conta.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-xl bg-transparent border-white/10">Cancelar</AlertDialogCancel>
                      <AlertDialogAction data-testid="retry-payouts-confirm" onClick={retryPayouts} className="rounded-xl bg-amber-600 hover:bg-amber-700">Reprocessar agora</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {/* Timeline */}
              <div data-testid="pix-timeline">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Linha do tempo</div>
                <div className="space-y-2">
                  {detail.timeline.map((t, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      {t.kind === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> : t.kind === "fail" ? <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" /> : <Circle className="h-3 w-3 text-zinc-500 mt-1 shrink-0" />}
                      <div><span className="mono text-xs text-zinc-500">{(t.at || "").slice(0, 19).replace("T", " ")}</span><div className={t.kind === "fail" ? "text-red-300" : "text-zinc-200"}>{t.label}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
